import { useMemo, useCallback, useRef, useEffect } from 'react';

import {
	DndContext,
	DragOverlay,
	PointerSensor,
	useSensor,
	useSensors,
	closestCenter,
	pointerWithin,
} from '@dnd-kit/core';
import PropTypes from 'prop-types';

import { usePreferencesStore } from '#lib/stores/preferencesStore';

import { useStageSummaries } from '../../../models/deals/queries';
import DealCard from '../DealCard/DealCard';
import LossReasonModal from '../LossReasonModal/LossReasonModal';

import StageColumn from './StageColumn';
import { useDealDragDrop } from './useDealDragDrop';

/**
 * Kanban board with per-stage lazy loading and drag-and-drop stage changes.
 *
 * Each StageColumn owns its own data lifecycle via useStageDealsInfinite and
 * orders its deals by next-activity due date. Columns report their loaded
 * deals upward via onDealsLoaded so the board can resolve which stage a
 * dragged card came from.
 *
 * Cards can be dragged between columns to change a deal's stage; the stage
 * transition fires in the background and the caches are reconciled on settle.
 * Within a column the order is fixed (by due date), so there is no manual
 * reordering.
 */

function kanbanCollision(args) {
	const pointerHits = pointerWithin(args);
	const stageHit = pointerHits.find((c) => c.data?.droppableContainer?.data?.current?.type === 'stage');
	if (stageHit) return [stageHit];

	const centerHits = closestCenter(args);
	const closestStage = centerHits.find((c) => c.data?.droppableContainer?.data?.current?.type === 'stage');
	return closestStage ? [closestStage] : [];
}

const KanbanBoard = ({ pipeline, filters = {}, onOpenDeal, onNewDeal }) => {
	const boardScrollRef = useRef(null);
	const scrollRAF = useRef(null);
	const stageScrollsRef = useRef({});

	// Per-stage deal data reported by StageColumns
	const stageDealsRef = useRef(new Map());

	const stages = useMemo(
		() => [...(pipeline.stages ?? [])].sort((a, b) => a.stageOrder - b.stageOrder),
		[pipeline.stages],
	);

	const {
		activeDeal,
		handleDragStart,
		handleDragEnd,
		handleDragCancel,
		lostPrompt,
		lostSubmitting,
		confirmLostTransition,
		cancelLostTransition,
	} = useDealDragDrop({
		pipelineId: pipeline.id,
		filters,
		stageDealsRef,
		stages,
	});

	// Stage summaries for header stats (count, totalValue, avgProb).
	// Loading state is intentionally not surfaced — columns fall back to loaded-deal aggregates.
	const { data: summariesData } = useStageSummaries(pipeline.id, filters);

	const summariesByStage = useMemo(() => {
		const map = new Map();
		for (const s of summariesData?.summaries ?? []) {
			map.set(s.stageId, s);
		}
		return map;
	}, [summariesData]);

	// Callback for StageColumn to report its loaded deals
	const handleDealsLoaded = useCallback((stageId, deals) => {
		stageDealsRef.current.set(stageId, {
			dealIds: deals.map((d) => d.id),
			dealMap: new Map(deals.map((d) => [d.id, d])),
		});
	}, []);

	// Scroll persistence
	const savedScroll = usePreferencesStore((s) => s.kanbanScrollPositions[pipeline.id]);
	const setKanbanScroll = usePreferencesStore((s) => s.setKanbanScroll);

	const savedScrollRef = useRef(savedScroll);
	useEffect(() => {
		if (boardScrollRef.current && savedScrollRef.current?.horizontal) {
			boardScrollRef.current.scrollLeft = savedScrollRef.current.horizontal;
		}
	}, []);

	const persistScroll = useCallback(() => {
		setKanbanScroll(pipeline.id, {
			horizontal: boardScrollRef.current?.scrollLeft ?? 0,
			stages: { ...stageScrollsRef.current },
		});
	}, [pipeline.id, setKanbanScroll]);

	const handleBoardScroll = useCallback(() => {
		if (scrollRAF.current) return;
		scrollRAF.current = requestAnimationFrame(() => {
			persistScroll();
			scrollRAF.current = null;
		});
	}, [persistScroll]);

	const handleStageScroll = useCallback(
		(stageId, scrollTop) => {
			stageScrollsRef.current[stageId] = scrollTop;
			persistScroll();
		},
		[persistScroll],
	);

	useEffect(
		() => () => {
			if (scrollRAF.current) cancelAnimationFrame(scrollRAF.current);
		},
		[],
	);

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

	if (stages.length === 0) {
		return (
			<div className="bg-white rounded-card border-2 border-dashed border-bgBase-600 p-12 text-center text-bgBase-800">
				Diese Pipeline hat noch keine Stages. Legen Sie welche in den Einstellungen an.
			</div>
		);
	}

	return (
		<>
			<DndContext
				sensors={sensors}
				collisionDetection={kanbanCollision}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
				onDragCancel={handleDragCancel}
			>
				<div
					ref={boardScrollRef}
					onScroll={handleBoardScroll}
					className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden -mx-6 px-6 pb-3"
				>
					<div className="flex gap-3.5 min-w-max h-full pt-3.5">
						{stages.map((stage, stageIndex) => (
							<StageColumn
								key={stage.id}
								stage={stage}
								stageIndex={stageIndex}
								pipelineId={pipeline.id}
								filters={filters}
								stageSummary={summariesByStage.get(stage.id)}
								onDealsLoaded={handleDealsLoaded}
								onOpenDeal={onOpenDeal}
								onNewDeal={onNewDeal}
								initialScrollTop={savedScroll?.stages?.[stage.id]}
								onScrollChange={handleStageScroll}
							/>
						))}
					</div>
				</div>
				<DragOverlay>
					{activeDeal ? (
						<div className="opacity-95 rotate-1 scale-105 rounded-card shadow-elevated">
							<DealCard deal={activeDeal} />
						</div>
					) : null}
				</DragOverlay>
			</DndContext>
			<LossReasonModal
				isOpen={Boolean(lostPrompt)}
				onClose={cancelLostTransition}
				onConfirm={confirmLostTransition}
				dealTitle={lostPrompt?.movedDeal?.title}
				isLoading={lostSubmitting}
			/>
		</>
	);
};

KanbanBoard.propTypes = {
	pipeline: PropTypes.object.isRequired,
	filters: PropTypes.object,
	onOpenDeal: PropTypes.func,
	onNewDeal: PropTypes.func,
};

export default KanbanBoard;
